import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Chess } from "chess.js";
import { api } from "../lib/api";
import Board from "../components/Board";
import { Sounds } from "../lib/sounds";
import { Play, Pause, SkipBack } from "lucide-react";

export default function TrapView() {
  const { trapId } = useParams();
  const [trap, setTrap] = useState(null);
  const [ply, setPly] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [chess, setChess] = useState(new Chess());
  const [flash, setFlash] = useState({});

  useEffect(() => {
    api.get(`/opening-traps/${trapId}`).then(({ data }) => setTrap(data)).catch(() => setTrap(false));
  }, [trapId]);

  useEffect(() => {
    // reset when trap changes
    setChess(new Chess());
    setPly(0);
    setPlaying(false);
    setFlash({});
  }, [trapId]);

  const step = () => {
    if (!trap || ply >= trap.moves.length) return;
    const c = new Chess(chess.fen());
    let move;
    try {
      move = c.move(trap.moves[ply]);
    } catch {
      try {
        move = c.move(trap.moves[ply].replace(/[?!+#]/g, ""));
      } catch {
        setPlaying(false);
        return;
      }
    }
    if (!move) { setPlaying(false); return; }
    if (move.captured) Sounds.capture(); else Sounds.move();
    setFlash({
      [move.from]: { background: "rgba(198,161,91,0.35)" },
      [move.to]: { background: "rgba(198,161,91,0.55)" },
    });
    setChess(c);
    setPly(ply + 1);
  };

  useEffect(() => {
    if (!playing || !trap) return;
    if (ply >= trap.moves.length) { setPlaying(false); return; }
    const id = setTimeout(() => step(), 1500);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [playing, ply, trap]);

  const reset = () => {
    setChess(new Chess());
    setPly(0);
    setPlaying(false);
    setFlash({});
  };

  if (trap === false) return <div className="p-12 text-center text-[#A04A3C]">Trap not found.</div>;
  if (!trap) return <div className="p-12 text-center text-[#A8A196]">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="overline mb-2">Opening trap</div>
      <h1 className="font-serif text-3xl sm:text-4xl text-[#EFE8D8] mb-2">{trap.name}</h1>
      <p className="text-[#A8A196] mb-8 max-w-2xl">{trap.description}</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <Board position={chess.fen()} arePiecesDraggable={false} customSquareStyles={flash} />
        </div>
        <div className="lg:col-span-4 space-y-4">
          <div className="card-panel p-4">
            <div className="flex gap-2 mb-4">
              <button data-testid="trap-play" onClick={() => setPlaying(!playing)} className="btn-brass flex-1 justify-center">
                {playing ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Play</>}
              </button>
              <button data-testid="trap-reset" onClick={reset} className="btn-ghost"><SkipBack size={14} /></button>
              <button data-testid="trap-step" onClick={step} className="btn-ghost">Step</button>
            </div>
            <div className="overline mb-2">Move {ply}/{trap.moves.length}</div>
            <div className="font-mono text-sm text-[#EFE8D8] mb-3" data-testid="trap-current-move">
              {ply > 0 ? trap.moves[ply - 1] : "—"}
            </div>
            <p className="text-sm text-[#A8A196] leading-relaxed" data-testid="trap-explanation">
              {ply > 0 ? trap.explanations[ply - 1] : "Press play to begin."}
            </p>
          </div>

          <div className="card-panel p-4">
            <div className="overline mb-3">All moves</div>
            <div className="font-mono text-xs text-[#A8A196] space-y-1">
              {trap.moves.map((m, i) => {
                const num = Math.floor(i / 2) + 1;
                const isWhite = i % 2 === 0;
                return (
                  <span key={i} className={`inline-block mr-2 ${i < ply ? "text-[#C6A15B]" : ""}`}>
                    {isWhite && `${num}.`} {m}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
